import { useState } from 'react';
import { Flex, Group } from '@mantine/core';
import useRetrieveAutomations from '@/hooks/api/automations/useRetrieveAutomations.ts';

import AutomationsTable from '@/pages/Automations/components/AutomationsTable.tsx';
import CustomSpinner from '@/components/Spinner.tsx';
import TextSearchBox from '@/components/TextSearchBox.tsx';
import AutomationStats, {
  AutomationFilter,
} from '@/pages/Automations/components/AutomationStats.tsx';
import PageHeaderText from '@/components/PageHeaderText.tsx';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import CreateAutomationModal from '@/components/CreateAutomationModal/CreateAutomationModal';
import AppIcon from '@/components/AppIcon.tsx';
import { HiOutlinePlus } from 'react-icons/hi2';
import AutomationPageManualRunButton from '@/pages/AutomationPage/components/ManualRunModal/components/AutomationPageManualRunButton.tsx';
import { ExecutionStatus } from '@/types/statuses.ts';
import { Automation } from '@/types/automations.ts';

const filterAutomationType = (data: Automation[], filter: AutomationFilter) => {
  switch (filter) {
    case 'All':
      return data;
    case 'Completed':
      return data.filter((item) => item.latest_run_status === ExecutionStatus.Completed);
    case 'Failed':
      return data.filter((item) => item.latest_run_status === ExecutionStatus.Errors);
    default:
      return data;
  }
};

const fullTextSearch = (data: Automation[], search: string) => {
  if (!search) {
    return data;
  }
  return data.filter((item) => {
    return item.name.toLowerCase().includes(search.toLowerCase());
  });
};

export function Automations() {
  const { data: automations, isPending: isLoadingAutomations } = useRetrieveAutomations();

  const [automationFilter, setAutomationFilter] = useState<AutomationFilter>('All');

  const [searchQuery, setSearchQuery] = useState('');

  const [showAutomationModal, setShowAutomationModal] = useState(false);

  const filteredAutomations = filterAutomationType(
    fullTextSearch(automations?.results || [], searchQuery),
    automationFilter,
  );

  return (
    <>
      <Flex justify="space-between" w="100%" align={'center'}>
        <PageHeaderText title="Automations" />
        <Group gap={'xs'}>
          <AutomationPageManualRunButton variant={'outline'} />

          <StyledButton
            leftSection={
              <AppIcon Icon={HiOutlinePlus} size={'16px'} iconProps={{ strokeWidth: '2px' }} />
            }
            styles={{ section: { marginRight: '6px' } }}
            onClick={() => setShowAutomationModal(true)}
          >
            New Automation
          </StyledButton>
        </Group>
        <CreateAutomationModal
          showModal={showAutomationModal}
          setShowModal={setShowAutomationModal}
        />
      </Flex>

      {isLoadingAutomations ? (
        <CustomSpinner />
      ) : (
        <>
          <AutomationStats
            automations={automations?.results || []}
            automationFilter={automationFilter}
            setAutomationFilter={setAutomationFilter}
          />
          <Flex mt={'60px'} mb="lg" gap={'md'} align={'center'}>
            <TextSearchBox
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              placeholder={'Search automations'}
            />
          </Flex>
          <AutomationsTable automations={filteredAutomations} />
        </>
      )}
    </>
  );
}

export default Automations;
